import { filesRedirectTarget, linkedFileUuid, type LocatedAsset } from '../filesRedirectTarget';

/**
 * What a `promote/promoteWork` link shows (owner, 2026-10-11: Work is a menu item and a page
 * again). Pure, so the rules are tested without React.
 *
 * The Work page shows for:
 * - the plain route (the menu item), with or without `accountId`;
 * - `state=working` or `state=failed` (the "work failed" email and push, "See the progress"): the
 *   work of the song of the link when it names one;
 * - `result=<uuid>` or `reel=<uuid>` of a file that is queued, made now or an open failure;
 * - a uuid that the artist does not have, when the link names no song.
 *
 * Every other link opens the finished file in its home, as before (`filesRedirectTarget`):
 * - `result=<uuid>` / `reel=<uuid>` of a finished file: the Kit tab of the release for a keeper,
 *   Posts for a post, the archive for an archived post, each with the viewer open;
 * - `catalogItemUuid=&kind=keeper|post` (the main button of a "ready" email): the Kit tab or the
 *   Posts tab of the release;
 * - `catalogItemUuid=` and nothing else (the emails before 2026-10-10): the release page.
 *
 * The emails and the pushes keep the `promoteWork` path and its parameters: the native apps open
 * their own Work screen from the same link. Do not change the parameters here without the apps.
 */
export type WorkLinkDecision = { kind: 'page' } | { kind: 'redirect'; to: string };

export interface WorkLinkInput {
  search: string;
  /** The file of the uuid of the link; null when the artist has none; undefined with no uuid. */
  located: LocatedAsset | null | undefined;
  /** True when the file of the link is queued, made now or an open failure. */
  isBusy: boolean;
}

/** True for `state=working` and `state=failed`: the link asks for the work, not for a file. */
export function asksForWork(search: string): boolean {
  const state = new URLSearchParams(search).get('state');
  return state === 'working' || state === 'failed';
}

/** True when the decision needs `assets.locate` and the lists of the work: a uuid and no work state. */
export function workLinkNeedsFile(search: string): boolean {
  return linkedFileUuid(search) !== '' && !asksForWork(search);
}

export function workLinkDecision({ search, located, isBusy }: WorkLinkInput): WorkLinkDecision {
  if (asksForWork(search)) {
    return { kind: 'page' };
  }
  const song = new URLSearchParams(search).get('catalogItemUuid') ?? '';
  if (linkedFileUuid(search) !== '') {
    const hasFile = located !== undefined && located !== null;
    if ((hasFile && isBusy) || (!hasFile && song === '')) {
      return { kind: 'page' };
    }
    return { kind: 'redirect', to: filesRedirectTarget({ search, located, busyKinds: undefined }) };
  }
  if (song === '') {
    return { kind: 'page' };
  }
  return { kind: 'redirect', to: filesRedirectTarget({ search, located: undefined, busyKinds: undefined }) };
}
